import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import styles from './DrVehicleTracker.module.scss';
import { DRVehicleTrackerWrapper } from 'components/Map/DRVehicleTrackerWrapper';
import { useMobileViewport } from 'hooks/useMobileViewport';
import ReactDOMServer from 'react-dom/server';
import L from 'leaflet';
import { useSamsaraActions } from 'hooks/Samsara/useSamsaraActions';
import { useSamsaraSelector } from 'hooks/Samsara/useSamsaraSelector';

import TruckIcon from 'assets/images/truck.png';
//import TruckIconSelected from 'assets/images/markers/TruckMarkerSelected.svg';

const TruckCard = ({ truck, onClick, isSelected }) => {
  return (
    <div
      onClick={() => onClick(truck)}
      style={{
        padding: '12px',
        marginRight: '10px',
        marginLeft: '10px',
        border: `2px solid ${isSelected ? '#007bff' : '#e9ecef'}`,
        borderRadius: '8px',
        backgroundColor: isSelected ? '#f8f9fa' : 'white',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        fontSize: '13px',
        boxShadow: isSelected ? '0 2px 8px rgba(0,123,255,0.2)' : '0 1px 3px rgba(0,0,0,0.1)'
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.borderColor = '#007bff';
          e.currentTarget.style.backgroundColor = '#f8f9fa';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.borderColor = '#e9ecef';
          e.currentTarget.style.backgroundColor = 'white';
          e.currentTarget.style.transform = 'translateY(0)';
        }
      }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h4
            style={{
              margin: '0 0 6px 0',
              color: '#2c3e50',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
            <img src={TruckIcon} alt="Truck" style={{ width: 16, height: 16 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {truck.name}
            </span>
          </h4>

          <p
            style={{
              margin: '0 0 4px 0',
              fontSize: '11px',
              color: '#6c757d',
              fontFamily: 'monospace'
            }}>
            ID: {truck.id}
          </p>

          <p
            style={{
              margin: '0',
              fontSize: '11px',
              color: '#495057',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
            📍 {truck.address}
          </p>
        </div>

        <div
          style={{
            marginLeft: '12px',
            padding: '8px',
            backgroundColor: isSelected ? '#007bff' : '#28a745',
            color: 'white',
            borderRadius: '50%',
            minWidth: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 'bold',
            flexShrink: 0
          }}>
          {isSelected ? '✓' : truck.name.charAt(0).toUpperCase()}
        </div>
      </div>
    </div>
  );
};

const TruckSidebarContent = ({ truck, onClose }) => {
  if (!truck) return <div style={{ padding: '20px' }}>No truck selected</div>;

  return (
    <div style={{ padding: '24px', height: '100%', overflow: 'auto' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '24px'
        }}>
        <h2
          style={{
            margin: 0,
            color: '#2c3e50',
            fontSize: '20px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
          <img src={TruckIcon} alt="Truck" style={{ width: 20, height: 20 }} />
          {truck.name}
        </h2>

        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#6c757d',
            padding: '4px',
            borderRadius: '4px',
            lineHeight: 1
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f8f9fa';
            e.target.style.color = '#dc3545';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = '#6c757d';
          }}>
          ×
        </button>
      </div>

      <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
        <div
          style={{
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
          <h3
            style={{ margin: '0 0 12px 0', color: '#495057', fontSize: '16px', fontWeight: '600' }}>
            <img
              src={TruckIcon}
              alt="Truck"
              style={{ width: 20, height: 20, verticalAlign: 'middle' }}
            />{' '}
            Vehicle Information
          </h3>
          <div style={{ display: 'grid', gap: '8px' }}>
            <div>
              <strong>ID:</strong>{' '}
              <span style={{ fontFamily: 'monospace', color: '#6c757d' }}>{truck.id}</span>
            </div>
            <div>
              <strong>Name:</strong> <span style={{ color: '#495057' }}>{truck.name}</span>
            </div>
            <div>
              <strong>VIN:</strong>{' '}
              <span style={{ fontFamily: 'monospace', color: '#6c757d' }}>
                {truck.vin || 'N/A'}
              </span>
            </div>
          </div>
        </div>

        <div
          style={{
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: '#e3f2fd',
            borderRadius: '8px',
            border: '1px solid #bbdefb'
          }}>
          <h3
            style={{ margin: '0 0 12px 0', color: '#1565c0', fontSize: '16px', fontWeight: '600' }}>
            📍 Current Location
          </h3>
          <div style={{ display: 'grid', gap: '8px' }}>
            <div>
              <strong>Address:</strong> <span style={{ color: '#1976d2' }}>{truck.address}</span>
            </div>
            <div>
              <strong>Latitude:</strong>{' '}
              <span style={{ fontFamily: 'monospace', color: '#1565c0' }}>
                {truck.latitude?.toFixed(6)}
              </span>
            </div>
            <div>
              <strong>Longitude:</strong>{' '}
              <span style={{ fontFamily: 'monospace', color: '#1565c0' }}>
                {truck.longitude?.toFixed(6)}
              </span>
            </div>
          </div>
        </div>

        <div
          style={{
            padding: '16px',
            backgroundColor: '#fff3e0',
            borderRadius: '8px',
            border: '1px solid #ffcc02'
          }}>
          <h3
            style={{ margin: '0 0 12px 0', color: '#ef6c00', fontSize: '16px', fontWeight: '600' }}>
            🕒 Timing Information
          </h3>
          <div>
            <strong>Last Update:</strong>{' '}
            <span style={{ color: '#f57c00' }}>{new Date(truck.lastUpdate).toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const PopupContent = ({ info }) => (
  <div style={{ padding: '12px', minWidth: '220px' }}>
    <h4
      style={{
        margin: '0 0 12px 0',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
      <img src={TruckIcon} alt="Truck" style={{ width: 20, height: 20 }} />
      {info.name}
    </h4>
    <div style={{ display: 'grid', gap: '8px', fontSize: '13px' }}>
      <div>
        <strong>ID:</strong> <span style={{ fontFamily: 'monospace' }}>{info.id}</span>
      </div>
      <div>
        <strong>VIN:</strong> <span style={{ fontFamily: 'monospace' }}>{info.vin || 'N/A'}</span>
      </div>
      <div>
        <strong>Location:</strong> {info.address}
      </div>
      <div>
        <strong>Coords:</strong> {info.latitude.toFixed(4)}, {info.longitude.toFixed(4)}
      </div>
      <div>
        <strong>Last Update:</strong> {new Date(info.lastUpdate).toLocaleString()}
      </div>
    </div>
  </div>
);

function DrVehicleTracker() {
  const isMobile = useMobileViewport();
  const [hasInitialized, setHasInitialized] = useState(false);
  const [selectedTruckId, setSelectedTruckId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTruckList, setShowTruckList] = useState(true);
  const [showMap, setShowMap] = useState(true);
  const [sidebarData, setSidebarData] = useState({
    openSidebar: false,
    truck: null
  });
  const [leafletMapInstance, setLeafletMapInstance] = useState(null);
  const { getSamsaraGpsCoordinatesAction, getSamsaraTrucksAction } = useSamsaraActions();
  const samsaraState = useSamsaraSelector();

  const gpsData = Array.isArray(samsaraState?.gpsData) ? samsaraState.gpsData : [];
  const isLoading = Boolean(samsaraState?.isLoadingGps);
  const error = samsaraState?.gpsError || null;

  const [clusterGroup, setClusterGroup] = useState(null);

  const markerRefs = useRef({});

  const trucks = useMemo(() => {
    const samsaraTrucks = Array.isArray(samsaraState?.trucks) ? samsaraState.trucks : [];

    // 🚫 Don't build until both GPS and truck data are present
    if (!Array.isArray(gpsData) || gpsData.length === 0) {
      return [];
    }

    // Normalization helper
    const normalize = (str) =>
      String(str || '')
        .trim()
        .toLowerCase()
        .replace(/^#+/, '') // Remove leading hash
        .replace(/[\s-_]/g, ''); // Only remove spaces, dashes, underscores

    const validTrucks = gpsData
      .map((record, index) => {
        try {
          const lat = parseFloat(record?.samsaraGpsLatitude);
          const lng = parseFloat(record?.samsaraGpsLongitude);

          if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
            const truckId = record?.samsaraGpsTruckId || `truck-${index}`;

            const gpsName = record?.samsaraGpsTruckName;
            const gpsNameNormalized = normalize(gpsName);

            let matchingTruck = samsaraTrucks.find(
              (t) => normalize(t?.samsaraTruckName) === gpsNameNormalized
            );

            if (!matchingTruck) {
              matchingTruck = samsaraTrucks.find((t) => {
                const name = normalize(t?.samsaraTruckName);
                return (
                  gpsNameNormalized.startsWith(name) ||
                  gpsNameNormalized.endsWith(name) ||
                  name.startsWith(gpsNameNormalized) ||
                  name.endsWith(gpsNameNormalized)
                );
              });
            }

            const resolvedVin = matchingTruck?.samsaraTruckVin || null;

            if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
              // even if no VIN match, still show
              return {
                id: truckId,
                name: gpsName || `Truck ${index + 1}`,
                latitude: lat,
                longitude: lng,
                address: record?.samsaraGpsAddressFull || 'Location not available',
                lastUpdate: record?.samsaraGpsTime || new Date().toISOString(),
                vin: resolvedVin // may be null
              };
            }
          }

          return null;
        } catch (error) {
          console.error(`Error processing GPS record ${index}:`, error);
          return null;
        }
      })
      .filter((truck) => truck !== null);

    return validTrucks;
  }, [gpsData, samsaraState?.trucks]);

  const filteredTrucks = useMemo(() => {
    if (!searchTerm.trim()) return trucks;

    const searchLower = searchTerm.toLowerCase();
    return trucks.filter(
      (truck) =>
        truck.name?.toLowerCase().includes(searchLower) ||
        truck.id?.toLowerCase().includes(searchLower) ||
        truck.address?.toLowerCase().includes(searchLower)
    );
  }, [trucks, searchTerm]);

  const markers = useMemo(() => {
    const locationCounts = {};
    const jitterDistance = 0.00005;

    return filteredTrucks.map((truck) => {
      const key = `${truck.latitude},${truck.longitude}`;

      // Track how many times we've seen this lat/lng combo
      if (!locationCounts[key]) {
        locationCounts[key] = 0;
      } else {
        locationCounts[key] += 1;
      }

      const offsetIndex = locationCounts[key];
      const angle = (offsetIndex * 45 * Math.PI) / 180; // spread in 45° intervals

      const latOffset = jitterDistance * Math.sin(angle);
      const lngOffset = jitterDistance * Math.cos(angle);

      return {
        position: [truck.latitude + latOffset, truck.longitude + lngOffset],
        icon: TruckIcon,
        info: truck,
        label: truck.name
      };
    });
  }, [filteredTrucks]);

  /*const legend = useMemo(
    () => [
      { label: 'Active Truck', icon: TruckIcon },
      { label: 'Selected Truck', icon: TruckIcon }
    ],
    []
  );*/

  useEffect(() => {
    if (!hasInitialized) {
      getSamsaraGpsCoordinatesAction();
      getSamsaraTrucksAction();
      setHasInitialized(true);
    }
  }, [hasInitialized, getSamsaraGpsCoordinatesAction, getSamsaraTrucksAction]);

  const handleTruckClick = useCallback(
    (truck, markerInstance = null) => {
      const marker = markerInstance || markerRefs.current[truck.id];
      if (!marker || !leafletMapInstance) return;

      // 1. select in sidebar
      setSelectedTruckId(truck.id);
      setSidebarData({ openSidebar: !isMobile, truck });

      // 2. bring to front
      Object.values(markerRefs.current).forEach((m) => m.setZIndexOffset(2000));
      marker.setZIndexOffset(10000);

      // helper to pan/fly
      const fly = () =>
        leafletMapInstance.flyTo(marker.getLatLng(), 18, {
          animate: true,
          duration: 1.5
        });

      // 3. if in a cluster, zoom & then open popup
      if (
        clusterGroup &&
        typeof clusterGroup.zoomToShowLayer === 'function' &&
        clusterGroup.hasLayer(marker)
      ) {
        clusterGroup.zoomToShowLayer(marker, () => {
          //marker.openPopup();
          fly();
        });
      } else {
        // otherwise open immediately
        //marker.openPopup();
        fly();
      }
    },
    [isMobile, leafletMapInstance, clusterGroup]
  );

  const closeSidebar = useCallback(() => {
    setSelectedTruckId(null);
    setSidebarData({
      openSidebar: false,
      truck: null
    });
  }, []);

  const handleRefresh = useCallback(() => {
    getSamsaraGpsCoordinatesAction();
  }, [getSamsaraGpsCoordinatesAction]);

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);

  const toggleTruckList = useCallback(() => {
    setShowTruckList((prev) => !prev);
  }, []);

  const toggleMap = useCallback(() => {
    setShowMap((prev) => !prev);
  }, []);

  const handlePopupContent = useCallback((info) => {
    return <PopupContent info={info} />;
  }, []);

  return (
    <div className={styles.container}>
      <header
        style={{
          padding: '16px 24px',
          background: '#fff',
          borderBottom: '1px solid #e9ecef',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
          <h1
            style={{
              margin: 0,
              fontSize: '24px',
              color: '#2c3e50',
              fontWeight: '700'
            }}>
            <img
              src={TruckIcon}
              alt="Truck"
              style={{ width: 28, height: 28, verticalAlign: 'middle' }}
            />{' '}
            DR Vehicle Tracker
          </h1>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={toggleTruckList}
              style={{
                padding: '8px 16px',
                background: showTruckList ? '#28a745' : '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}>
              {showTruckList ? (isMobile ? '🗺️ Map' : '📋 Hide List') : '📋 Show List'}
            </button>

            <button
              onClick={toggleMap}
              style={{
                padding: '8px 16px',
                background: showMap ? '#17a2b8' : '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}>
              {showMap ? '🗺️ Hide Map' : '🗺️ Show Map'}
            </button>

            <button
              onClick={handleRefresh}
              disabled={isLoading}
              style={{
                padding: '8px 16px',
                background: isLoading ? '#6c757d' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                opacity: isLoading ? 0.7 : 1
              }}>
              {isLoading ? '🔄 Loading...' : '🔄 Refresh'}
            </button>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '16px',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
          <span
            style={{
              background: '#e3f2fd',
              color: '#1565c0',
              padding: '6px 12px',
              borderRadius: '16px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
            📊 {trucks.length} Total Trucks
          </span>

          <span
            style={{
              background: '#e8f5e8',
              color: '#2e7d32',
              padding: '6px 12px',
              borderRadius: '16px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
            🔍 {filteredTrucks.length} Showing
          </span>

          {selectedTruckId && (
            <span
              style={{
                background: '#fff3e0',
                color: '#ef6c00',
                padding: '6px 12px',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
              ✓ Selected
            </span>
          )}
        </div>

        {showTruckList && (
          <input
            type="text"
            placeholder="🔍 Search trucks by name, ID, or location..."
            value={searchTerm}
            onChange={handleSearchChange}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '2px solid #e9ecef',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'border-color 0.2s ease'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#007bff';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e9ecef';
            }}
          />
        )}
      </header>

      <main
        style={{
          display: 'grid',
          gridTemplateColumns:
            sidebarData.openSidebar && !isMobile && showTruckList && showMap
              ? '400px 1fr 360px'
              : sidebarData.openSidebar && !isMobile && showMap
              ? '1fr 360px'
              : sidebarData.openSidebar && !isMobile && showTruckList
              ? '400px 1fr 360px'
              : showTruckList && showMap && !isMobile
              ? '400px 1fr'
              : showTruckList && !showMap
              ? '1fr'
              : showMap && !showTruckList
              ? '1fr'
              : '1fr',
          height: 'calc(100vh - 160px)',
          overflow: 'hidden',
          position: 'relative',
          gap: '0',
          minHeight: 0
        }}>
        {showTruckList && (
          <aside
            className={styles.truckListSidebar}
            style={{
              height: '100%',
              maxHeight: 'calc(100vh - 160px)',
              overflowY: 'auto',
              overflowX: 'hidden'
            }}>
            {isLoading && trucks.length === 0 && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#6c757d'
                }}>
                <div
                  style={{
                    fontSize: '48px',
                    marginBottom: '16px',
                    animation: 'spin 2s linear infinite'
                  }}>
                  🔄
                </div>
                <h3 style={{ margin: '0 0 8px 0', color: '#495057' }}>Loading Trucks</h3>
                <p style={{ margin: 0 }}>Fetching GPS data from Samsara...</p>
              </div>
            )}

            {error && !isLoading && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#dc3545'
                }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
                <h3 style={{ margin: '0 0 8px 0', color: '#dc3545' }}>Error Loading Data</h3>
                <p style={{ margin: '0 0 16px 0', color: '#6c757d' }}>{error}</p>
                <button
                  onClick={handleRefresh}
                  style={{
                    padding: '8px 16px',
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}>
                  🔄 Try Again
                </button>
              </div>
            )}

            {!isLoading && !error && filteredTrucks.length > 0 && (
              <>
                <h3
                  style={{
                    margin: '10px 0 16px 10px',
                    color: '#495057',
                    fontSize: '16px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                  <img
                    src={TruckIcon}
                    alt="Truck"
                    style={{ width: 20, height: 20, verticalAlign: 'middle' }}
                  />{' '}
                  Active Trucks ({filteredTrucks.length})
                </h3>

                <div style={{ display: 'grid', gap: '4px' }}>
                  {filteredTrucks.map((truck) => (
                    <TruckCard
                      key={truck.id}
                      truck={truck}
                      onClick={handleTruckClick}
                      isSelected={selectedTruckId === truck.id}
                    />
                  ))}
                </div>
              </>
            )}

            {!isLoading && !error && trucks.length > 0 && filteredTrucks.length === 0 && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#6c757d'
                }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                <h3 style={{ margin: '0 0 8px 0', color: '#495057' }}>No Trucks Found</h3>
                <p style={{ margin: '0 0 16px 0' }}>Try adjusting your search terms</p>
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    padding: '8px 16px',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}>
                  Clear Search
                </button>
              </div>
            )}

            {!isLoading && !error && trucks.length === 0 && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#6c757d'
                }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚛</div>
                <h3 style={{ margin: '0 0 8px 0', color: '#495057' }}>No Trucks Available</h3>
                <p style={{ margin: '0 0 16px 0' }}>No GPS data found from Samsara</p>
                <button
                  onClick={handleRefresh}
                  style={{
                    padding: '8px 16px',
                    background: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}>
                  🔄 Refresh Data
                </button>
              </div>
            )}
          </aside>
        )}

        {showMap && (
          <section
            className={styles.mapContainer}
            style={{
              position: 'relative',
              overflow: 'hidden'
            }}>
            <DRVehicleTrackerWrapper
              markers={markers}
              popupContent={handlePopupContent}
              onMapReady={setLeafletMapInstance}
              onClusterReady={setClusterGroup}
              customMarkerRenderer={(marker, leafletMapInstance) => {
                const { info } = marker;
                const label = info?.name || 'Truck';

                // create your DivIcon
                const icon = L.divIcon({
                  className: '',
                  iconAnchor: [16, 42],
                  iconSize: [40, 72],
                  html: `
                    <div style="display:flex;flex-direction:column;align-items:center;text-align:center;height:72px;">
                      <div style="
                        margin-bottom:6px;
                        background:white;
                        font-size:11px;
                        font-weight:600;
                        padding:2px 6px;
                        border-radius:4px;
                        box-shadow:0 1px 2px rgba(0,0,0,0.2);
                        white-space:nowrap;
                      ">
                        ${label}
                      </div>
                      <img src="${marker.icon}" style="width:32px;height:32px;" />
                    </div>
                  `
                });

                // instantiate and bind popup
                const leafletMarker = L.marker(marker.position, {
                  icon,
                  interactive: true,
                  pane: 'markerPane',
                  zIndexOffset: 2000
                }).bindPopup(ReactDOMServer.renderToString(<PopupContent info={info} />), {
                  offset: [0, -50],
                  autoPan: false
                });

                // add to map & store ref
                leafletMarker.addTo(leafletMapInstance);
                markerRefs.current[info.id] = leafletMarker;

                // click just calls your handler (which now also opens the popup at the right time)
                leafletMarker.on('click', () => {
                  handleTruckClick(info, leafletMarker);
                });

                return leafletMarker;
              }}
            />

            <div className={styles.customZoomControls}>
              <button
                onClick={() => {
                  try {
                    const mapContainer = document.querySelector('.leaflet-container');
                    if (mapContainer && mapContainer._leaflet_map) {
                      mapContainer._leaflet_map.zoomIn();
                    }
                  } catch (error) {
                    console.error('Error during zoom in:', error);
                  }
                }}
                title="Zoom in"
                aria-label="Zoom in">
                +
              </button>
              <button
                onClick={() => {
                  try {
                    const mapContainer = document.querySelector('.leaflet-container');
                    if (mapContainer && mapContainer._leaflet_map) {
                      mapContainer._leaflet_map.zoomOut();
                    }
                  } catch (error) {
                    console.error('Error during zoom out:', error);
                  }
                }}
                title="Zoom out"
                aria-label="Zoom out">
                −
              </button>
            </div>

            {filteredTrucks.length === 0 && isLoading && (
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  padding: '20px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  zIndex: 1000,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  pointerEvents: 'none'
                }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔄</div>
                <div>Loading trucks...</div>
              </div>
            )}

            {filteredTrucks.length === 0 && !isLoading && !error && (
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  padding: '20px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  zIndex: 1000,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  pointerEvents: 'none'
                }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>🚛</div>
                <div>No trucks to display</div>
              </div>
            )}
          </section>
        )}

        {!showMap && !showTruckList && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              backgroundColor: '#f8f9fa',
              color: '#6c757d',
              fontSize: '18px',
              flexDirection: 'column',
              gap: '16px'
            }}>
            <div style={{ fontSize: '64px' }}>🚛</div>
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 8px 0', color: '#495057' }}>Both Views Hidden</h3>
              <p style={{ margin: 0 }}>Use the buttons above to show the truck list or map</p>
            </div>
          </div>
        )}
        {sidebarData.openSidebar && !isMobile && (
          <aside className={styles.detailsSidebar}>
            <TruckSidebarContent truck={sidebarData.truck} onClose={closeSidebar} />
          </aside>
        )}
      </main>

      {sidebarData.truck && isMobile && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            padding: '20px'
          }}>
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              maxWidth: '95%',
              maxHeight: '85%',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              display: 'flex',
              flexDirection: 'column'
            }}>
            <TruckSidebarContent truck={sidebarData.truck} onClose={closeSidebar} />
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default DrVehicleTracker;
